import { useNavigate } from "react-router-dom";
import React, { useState, useMemo, useEffect, useRef } from "react";

import { CandidateStatus, CompanyJobOpening } from "@/types";
import { useMyCompanyQuery } from "@/queries/company/my-company";

import {
  CalendarCheck,
  Edit,
  EllipsisVertical,
  FileCheck2,
  Headset,
  Info,
  Loader2,
  RefreshCw,
  UserCheck,
  Video,
  X,
} from "lucide-react";
import { Button } from "../ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "../ui/dropdown-menu";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "../ui/dialog";
import { ScrollArea } from "../ui/scroll-area";
import { Tooltip } from "../ui/tooltip";
import { useCompanyScheduleInterviewSheet } from "../dialogs/company/schedule-interview/store";
import { RejectCandidateOpportunityDialog } from "../dialogs/opportunity/reject";
import ChatWindow, { ChatWindowDialogHandle } from "../forms/chat";
import CreateUpdateInterviewDialog, {
  CreateUpdateInterviewDialogHandle,
} from "../forms/company/interview";
import CompanyCreateOfferForm from "../forms/offer/create";
import ViewOfferCompany from "../dialogs/company/opportunity/offer";
import { ViewPaymentsCompany } from "../dialogs/opportunity/payments";
import { ViewMilestonesCompany } from "../dialogs/opportunity/milestones";

import { useConversationsQuery } from "@/queries/chat/user-conversation";
import { useCompleteInterview } from "@/mutations/company/opening/complete-interview";
import { useCancelInterview } from "@/mutations/company/opening/cancel-interview";
import { useDeleteInterview } from "@/mutations/company/opening/delete-interview";

export const CompanyCandidateControls = ({
  candidate,
  opportunity,
  chatRef,
  enableProfileLink,
}: {
  opportunity: CompanyJobOpening;
  candidate: CandidateStatus;
  chatRef?: React.RefObject<ChatWindowDialogHandle>;
  enableProfileLink?: boolean;
}) => {
  const navigate = useNavigate();

  const { chatConversations } = useConversationsQuery();
  const { company } = useMyCompanyQuery();

  const dialogRef = useRef<CreateUpdateInterviewDialogHandle>(null);

  const { setOpen, setOpportunity, setCandidate, setInterview, setIsUpdating } =
    useCompanyScheduleInterviewSheet();

  const { completeInterview, isCompleting } = useCompleteInterview();
  const { cancelInterview, isCancelling } = useCancelInterview();
  const { deleteInterview, isDeleting } = useDeleteInterview();

  const mostRecentInterview = useMemo(
    () => candidate.interviews.at(-1),
    [candidate.interviews],
  );

  const anonymousCandidate = useMemo(() => {
    return (
      candidate.status !== "Screening" &&
      candidate.status !== "Possible Fit" &&
      candidate.status !== "Shortlisted" &&
      candidate.status !== "Making Offer" &&
      candidate.status !== "Contracting" &&
      candidate.status !== "Accepted by Client" &&
      candidate.status !== "Rejected for Role"
    );
  }, [candidate.status]);

  const stage = useMemo<
    | "Rejected"
    | "Hired"
    | "Contracting"
    | "Offer Made"
    | `Interview${string}`
    | "Matched"
  >(() => {
    if (candidate.status === "Rejected for Role") {
      return "Rejected";
    } else if (candidate.status === "Accepted by Client") {
      return "Hired";
    } else if (candidate.status === "Contracting") {
      return "Contracting";
    } else if (candidate.status === "Making Offer") {
      return "Offer Made";
    } else if (!mostRecentInterview) {
      return "Matched";
    } else if (mostRecentInterview.isCompleted) {
      return `Interview "${mostRecentInterview.name}" Complete`;
    } else if (!mostRecentInterview.scheduled) {
      return `Interview "${mostRecentInterview.name}" Requested (Awaiting Scheduling)`;
    } else if (mostRecentInterview.startTime) {
      return `Interview "${mostRecentInterview.name}" Scheduled on ${new Date(mostRecentInterview.startTime).toLocaleString("en-US")}`;
    } else {
      return `Interview "${mostRecentInterview.name}" Scheduled`;
    }
  }, [mostRecentInterview, candidate]);

  // See if there is an existing conversation between the user and the company
  const conversation = chatConversations?.find(
    (conversation) =>
      conversation.jobOpeningId === opportunity.id &&
      conversation.participants.length === 2 &&
      conversation.participants.every(
        (participant) =>
          participant.companyId === company?.id ||
          participant.userId === candidate.userId,
      ),
  );

  const participants = company?.id
    ? {
        userId: candidate.userId,
        companyId: company.id,
      }
    : undefined;

  return (
    <div className="flex flex-row items-center gap-2 flex-wrap md:gap-4">
      {opportunity.status !== "Role Filled (won)" &&
        opportunity.status !== "Role Filled (lost)" &&
        stage !== "Rejected" && (
          <RejectCandidateOpportunityDialog
            openingId={opportunity.id}
            candidateStatusId={candidate.id}
            button={
              <Tooltip content="Reject candidate" asChild>
                <Button
                  variant="outline"
                  size="icon"
                  disabled={anonymousCandidate}
                >
                  <X />
                </Button>
              </Tooltip>
            }
          />
        )}
      {(conversation ?? participants) && (
        <ChatWindow
          conversation={conversation}
          participants={participants}
          jobOpeningId={opportunity.id}
          /* eslint-disable-next-line @typescript-eslint/prefer-nullish-coalescing */
          title={`Chat with ${candidate.name || "Company"}`}
          disabled={
            opportunity.status === "Role Filled (lost)" ||
            opportunity.status === "Role Filled (won)" ||
            anonymousCandidate
          }
          ref={chatRef}
        />
      )}
      {enableProfileLink && (
        <Tooltip
          content={
            anonymousCandidate
              ? "Once the candidate signals interest, you will be able to view their profile"
              : "See details"
          }
        >
          <Button
            variant="outline"
            size="icon"
            disabled={anonymousCandidate}
            onClick={() => {
              const url = new URL(window.location.href);
              url.searchParams.set("focus", opportunity.id);
              window.history.replaceState({}, "", url.toString());
              navigate(
                `/opening/${opportunity.id}/application/${candidate.id}`,
              );
            }}
          >
            <Info />
          </Button>
        </Tooltip>
      )}
      {opportunity.contracts.length > 0 &&
      opportunity.status === "Role Filled (won)" ? (
        <Button
          variant="outline"
          disabled={!opportunity.contracts[0]}
          onClick={() => {
            window.open(opportunity.contracts[0]!.link, "_blank");
          }}
        >
          <FileCheck2 />
          View Contract
        </Button>
      ) : stage === "Offer Made" ||
        stage === "Contracting" ||
        stage === "Hired" ? (
        <ViewOfferCompany candidate={candidate} opportunity={opportunity} />
      ) : opportunity.status !== "Role Filled (lost)" &&
        opportunity.status !== "Role Filled (won)" &&
        (stage.startsWith("Completed") ||
          stage.startsWith("Interview") ||
          stage === "Matched") ? (
        <>
          <MakeOfferDialog opportunity={opportunity} candidate={candidate} />
          {!mostRecentInterview || mostRecentInterview.isCompleted ? (
            <Button
              variant="outline"
              disabled={anonymousCandidate}
              onClick={() => setTimeout(() => dialogRef.current?.open(), 0)}
            >
              <Headset />
              Request Interview
            </Button>
          ) : mostRecentInterview.scheduled ? (
            <>
              <Button
                variant="outline"
                onClick={() => {
                  completeInterview({
                    openingId: opportunity.id,
                    candidateStatusId: candidate.candidateId,
                    interviewId: mostRecentInterview.id,
                  });
                }}
                disabled={isCompleting || anonymousCandidate}
              >
                {isCompleting ? (
                  <Loader2 className="animate-spin" />
                ) : (
                  <CalendarCheck />
                )}
                Complete Interview
              </Button>
              {mostRecentInterview.link && (
                <Button
                  variant="secondary"
                  onClick={() =>
                    window.open(mostRecentInterview.link, "_blank")
                  }
                >
                  <Video /> Join Interview
                </Button>
              )}
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button variant="outline" size="icon">
                    <EllipsisVertical />
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent sideOffset={8} className="mr-[2.5rem]">
                  <DropdownMenuItem
                    onClick={() => {
                      setOpportunity(opportunity);
                      setCandidate(candidate);
                      setInterview(mostRecentInterview);
                      setIsUpdating(true);
                      setOpen(true);
                    }}
                  >
                    <RefreshCw />
                    <span>Reschedule Interview</span>
                  </DropdownMenuItem>
                  <DropdownMenuItem
                    onClick={() => {
                      cancelInterview({
                        openingId: opportunity.id,
                        interviewId: mostRecentInterview.id,
                      });
                    }}
                    disabled={isCancelling || anonymousCandidate}
                  >
                    {isCancelling ? (
                      <Loader2 className="animate-spin" />
                    ) : (
                      <X />
                    )}
                    <span>Cancel Interview</span>
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            </>
          ) : (
            <>
              <Button
                variant="outline"
                disabled={anonymousCandidate}
                onClick={() => {
                  setOpportunity(opportunity);
                  setCandidate(candidate);
                  setInterview(mostRecentInterview);
                  setOpen(true);
                }}
              >
                <Headset />
                Schedule Interview
              </Button>
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button variant="outline" size="icon">
                    <EllipsisVertical />
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent sideOffset={8} className="mr-[2.5rem]">
                  <DropdownMenuItem
                    onClick={() =>
                      setTimeout(() => dialogRef.current?.open(), 0)
                    }
                  >
                    <Edit />
                    <span>Update Interview</span>
                  </DropdownMenuItem>
                  <DropdownMenuItem
                    onClick={() => {
                      deleteInterview({
                        openingId: opportunity.id,
                        interviewId: mostRecentInterview.id,
                      });
                    }}
                    disabled={isDeleting || anonymousCandidate}
                  >
                    {isDeleting ? <Loader2 className="animate-spin" /> : <X />}
                    <span>Withdraw Interview Request</span>
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            </>
          )}
        </>
      ) : null}
      <CreateUpdateInterviewDialog
        ref={dialogRef}
        jobOpeningId={opportunity.id}
        companyId={opportunity.companyRecordId}
        {...(!mostRecentInterview || mostRecentInterview.isCompleted
          ? { candidateId: candidate.candidateId }
          : { interview: mostRecentInterview })}
      />
      <ViewPaymentsCompany candidate={candidate} />
      <ViewMilestonesCompany
        jobOpeningId={opportunity.id}
        candidate={candidate}
      />
    </div>
  );
};

const MakeOfferDialog = ({
  candidate,
  opportunity,
}: {
  candidate: CandidateStatus;
  opportunity: CompanyJobOpening;
}) => {
  const [open, setOpen] = useState(false);

  useEffect(() => {
    if (candidate.status === "Making Offer") {
      setOpen(false);
    }
  }, [candidate.status]);

  const anonymousCandidate = useMemo(() => {
    return (
      candidate.status !== "Screening" &&
      candidate.status !== "Possible Fit" &&
      candidate.status !== "Shortlisted" &&
      candidate.status !== "Making Offer" &&
      candidate.status !== "Contracting" &&
      candidate.status !== "Accepted by Client" &&
      candidate.status !== "Rejected for Role"
    );
  }, [candidate.status]);

  const candidateFirstName = useMemo(
    () => candidate.name?.split(" ")[0] ?? "The Candidate",
    [candidate.name],
  );

  const otherCandidateOffered = useMemo(
    () =>
      opportunity.candidates?.some(
        ({ id, status }) =>
          (id !== candidate.id && status === "Making Offer") ||
          status === "Contracting" ||
          status === "Accepted by Client",
      ),
    [opportunity.candidates, candidate.id],
  );

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button variant="outline" disabled={anonymousCandidate}>
          <UserCheck />
          Make Offer
        </Button>
      </DialogTrigger>
      <DialogContent className="w-full max-w-[min(calc(100vw-2rem),768px)] p-0">
        <DialogHeader className="p-6">
          <div className="flex items-center gap-2">
            <DialogTitle className="text-3xl text-[#2E2E38] font-bold">
              Make An Offer To {candidateFirstName}
            </DialogTitle>
          </div>
          <DialogDescription className="text-base text-[#5C6060]">
            <b>
              Once you send this offer, the candidate will be notified and can
              accept or propose changes.
            </b>
            <br />
            After terms are agreed, the Venturous team will step in to finalize
            the contract and complete the hire.
            {otherCandidateOffered && (
              <>
                <br />
                <br />
                <b>
                  Our records indicate you have already hired or made an offer
                  to another candidate. Only proceed if you intend to hire this
                  candidate in addition to any candidate who has already
                  received an offer.
                </b>
              </>
            )}
          </DialogDescription>
        </DialogHeader>
        <ScrollArea className="max-h-[calc(100vh-10rem)] md:max-h-[70vh] px-6 pb-6">
          <CompanyCreateOfferForm
            jobOpening={opportunity}
            candidateStatus={candidate}
            onCancel={() => setOpen(false)}
            onSuccess={() => setOpen(false)}
          />
        </ScrollArea>
      </DialogContent>
    </Dialog>
  );
};
